<template>
  <div class="section">
    <h4>{{ name }} distribution</h4>
    <histogram-chart :chartData="histogramData" :options="histogramOptions">
    </histogram-chart>

    <b-table
      small
      fixed
      borderless
      :items="averages"
      thead-class="d-none"
      class="protein_data_row"
    ></b-table>

    <b-form @submit="onSubmit" inline class="mt-4">
      <b-form-group label="Bar width" label-for="bar-width" class="mr-2 ml-2">
        <b-form-input
          id="bar-width"
          type="number"
          min="0.1"
          step="0.1"
          size="sm"
          class="mr-2 ml-2"
          v-model="form.barWidth"
        ></b-form-input>
      </b-form-group>
      <b-button size="sm" type="submit" variant="secondary">Update</b-button>
    </b-form>
  </div>
</template>

<script>
import * as d3 from 'd3';
import HistogramChart from '../HistogramChart.js';
import utilities from '../utilities';

export default {
  name: 'Histogram',
  components: {
    HistogramChart,
  },
  created() {},
  props: {
    name: null,
    compoundData: null,
    barColor: null,
    barBorderColor: null,
  },
  data() {
    return {
      histogramOptions: null,
      histogramData: null,
      averages: null,
      form: {
        barWidth: 0.1,
      },
    };
  },
  watch: {
    compoundData: function(val, oldVal) {
      val == oldVal;
      this.compoundData = val;
      this.fillData();
    },
  },
  mounted() {},
  beforeUpdate() {},
  updated() {},
  methods: {
    onSubmit(evt) {
      console.log('Submit');
      evt.preventDefault();
      this.fillData();
    },
    fillData() {
      this.averages = this.getCompoundAveragesTableData(this.compoundData);
      const histogramData = this.createHistogramData(this.compoundData);

      this.histogramOptions = {
        title: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        maintainAspectRatio: false,
        animation: {
          duration: 5,
        },
        scales: {
          /*
          Two x-axis version are implemented but other one is hidden 
          Default histogram X-axis ticks are not in correct place. Should be start of the bar, not in the middle of the bar 
          NOTE: The second axis interval must be exactly same as the histogram max - min value. 
          */
          xAxes: [
            {
              position: 'bottom',
              display: true, // Shown!
              scaleLabel: {
                display: true,
                labelString: `${this.name} [%]`,
              },
              ticks: {
                callback: (value) => {
                  return Number(value).toFixed(1);
                },
              },
            },
            {
              type: 'linear',
              display: false, // Hidden!
              scaleLabel: {
                display: true,
                labelString: `${this.name} [%]`,
              },
              ticks: {
                beginAtZero: false,
                autoSkip: false,
                stepSize: 0.5,
                min: histogramData.min,
                max: histogramData.max,
                callback: (value) => {
                  return Number(value).toFixed(1);
                },
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                stepSize: 1,
              },
              scaleLabel: {
                display: true,
                labelString: 'Number of points',
              },
            },
          ],
        },
      };

      this.histogramData = {
        labels: histogramData.labels,
        datasets: [
          {
            label: 'Protein distribution',
            barPercentage: 1.2,
            data: histogramData.counts,
            backgroundColor: this.barColor,
            borderColor: this.barBorderColor,
            borderWidth: 0.5,
          },
        ],
      };
    },
    createHistogramData(compoundData) {
      var data = compoundData.map((data) => {
        return data.y;
      });

      // Calculate treshold for each bin
      const max = Math.max(...data);
      const min = Math.min(...data);
      let binsCount = (max - min) / this.form.barWidth;
      let tresholds = [];
      for (var i = 0; i < binsCount; i++) {
        tresholds.push(i * this.form.barWidth + min);
      }

      var x = d3.scaleLinear().domain(d3.extent(data));
      var histogram = d3
        .histogram()
        .domain(x.domain())
        .thresholds(tresholds);
      var bins = histogram(data);

      const binSize = bins[0].x1 - bins[0].x0;
      const minHistogramValue = bins[0].x0;
      const maxHistogramValue = bins[bins.length - 1].x1 + binSize;

      return {
        labels: bins.map((arr) => arr.x0),
        counts: bins.map((arr) => arr.length),
        min: minHistogramValue,
        max: maxHistogramValue,
      };
    },
    getCompoundAveragesTableData(compoundData) {
      var data = compoundData.map((data) => {
        return data.y;
      });

      return [
        {
          name: `${this.name} STD`,
          value: utilities.getStandardDeviation(data).toFixed(4),
        },
        {
          name: `${this.name} mean`,
          value: utilities.getMean(data).toFixed(4),
        },
        {
          name: `${this.name} median`,
          value: utilities.getMedian(data).toFixed(4),
        },
        {
          name: `${this.name} FWHM`,
          value: utilities.getFWHM(data).toFixed(4),
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.protein_data_row {
  text-align: left;
  width: 260px;
  font-size: 0.9fr;
}
</style>
